import { useEffect, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useIdentity } from '@/stores/identity';
import type { Identity } from '@/lib/types';

/**
 * AuthInitializer
 *
 * Bootstraps the identity store on app mount and keeps it in sync with the
 * Supabase auth session.
 *
 * Flow:
 *  1. On mount, FIRST check for an implicit-flow recovery hash. The Supabase
 *     JS client is configured with `flowType: 'pkce'`, which means it does
 *     NOT auto-parse hash-fragment sessions (PKCE expects `?code=...` in the
 *     query string). Dashboard-triggered recovery emails always come back
 *     with tokens in the hash fragment, so we have to bridge them manually:
 *     parse the hash, call setSession() to install the session, clear the
 *     hash, and redirect to /auth/reset. Without this, the recovery email
 *     link strands the user on the marketing page with no signal anything
 *     happened. (Decision log: docs/decisions/2026-05-12_pkce_recovery_bridge.md)
 *  2. Read the current session (post-bridge if applicable).
 *  3. If signed in, call public.app_identity() RPC to get the identity blob.
 *  4. Subscribe to onAuthStateChange.
 *     - SIGNED_IN / TOKEN_REFRESHED / USER_UPDATED: re-fetch identity.
 *     - SIGNED_OUT: drop to guest.
 *     - PASSWORD_RECOVERY: hard-redirect to /auth/reset (kept as a defensive
 *       safety net for non-dashboard recovery flows, e.g. app-initiated
 *       resetPasswordForEmail which uses PKCE correctly).
 *  5. Renders children unconditionally - the RouteGuard decides whether to
 *     show them based on the store status.
 */
export function AuthInitializer({ children }: { children: ReactNode }) {
  const setLoading = useIdentity((s) => s._setLoading);
  const setAuthenticated = useIdentity((s) => s._setAuthenticated);
  const setGuest = useIdentity((s) => s._setGuest);
  const setError = useIdentity((s) => s._setError);

  useEffect(() => {
    let cancelled = false;

    async function loadIdentity() {
      setLoading();

      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;

      if (!sessionData.session) {
        setGuest();
        return;
      }

      const { data, error } = await supabase.rpc('app_identity');
      if (cancelled) return;

      if (error) {
        setError(error.message);
        return;
      }

      setAuthenticated(data as Identity);
    }

    // Step 1: bridge implicit-flow recovery hash to the PKCE client.
    // See the AuthInitializer comment above for why this exists.
    async function bridgeRecoveryHash(): Promise<boolean> {
      if (typeof window === 'undefined') return false;
      const hash = window.location.hash;
      if (!hash || hash.length <= 1) return false;
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      if (params.get('type') !== 'recovery') return false;
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      if (!accessToken || !refreshToken) return false;

      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (cancelled) return true;
      if (error) {
        setError(error.message);
        // Stay on the page so the user sees an error rather than getting
        // bounced to /auth/reset with no session.
        return true;
      }
      // Strip the hash so a back-button / refresh doesn't re-trigger this
      // path with a token that's now consumed.
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );
      if (!window.location.pathname.startsWith('/auth/reset')) {
        window.location.replace('/auth/reset');
      }
      return true;
    }

    void (async () => {
      const bridged = await bridgeRecoveryHash();
      if (cancelled) return;
      // If we redirected via the bridge, the page is about to unload --
      // skip loadIdentity to avoid a brief flash of the wrong identity state.
      if (bridged) return;
      await loadIdentity();
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      if (cancelled) return;
      if (event === 'SIGNED_OUT') {
        setGuest();
        // Explicit sign-out should land on a clean /auth/sign-in (not preserve
        // the page we signed out from via `state.from`). Without this, signing
        // back in returns the user to where they signed out from -- sign-out
        // is a deliberate "fresh start" gesture. Hard-navigate so SignIn's
        // `from` defaults to '/' (Home).
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/')) {
          window.location.replace('/auth/sign-in');
        }
      } else if (event === 'PASSWORD_RECOVERY') {
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/reset')) {
          window.location.replace('/auth/reset');
        }
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        void loadIdentity();
      }
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [setLoading, setAuthenticated, setGuest, setError]);

  return <>{children}</>;
}
